import React, { createContext, useContext, useState, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Alert, Snackbar, useTheme } from '@mui/material';

const NotificationContext = createContext(null);

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const theme = useTheme();
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'info',
    duration: 3000  // Reduced from 6000ms to 3000ms (3 seconds)
  });

  const closeNotification = useCallback(() => {
    setNotification(prev => ({ ...prev, open: false }));
  }, []);

  const showNotification = useCallback((message, severity = 'info', duration = 3000) => {
    setNotification({
      open: true,
      message,
      severity,
      duration
    });
  }, []);

  const notifySuccess = useCallback((message, duration) => {
    showNotification(message, 'success', duration);
  }, [showNotification]);

  const notifyError = useCallback((message, duration) => {
    showNotification(message, 'error', duration);
  }, [showNotification]);

  const notifyInfo = useCallback((message, duration) => {
    showNotification(message, 'info', duration);
  }, [showNotification]);

  const notifyWarning = useCallback((message, duration) => {
    showNotification(message, 'warning', duration);
  }, [showNotification]);

  const value = useMemo(() => ({
    notification,
    showNotification,
    notifySuccess,
    notifyError,
    notifyInfo,
    notifyWarning,
    closeNotification
  }), [
    notification,
    showNotification,
    notifySuccess,
    notifyError,
    notifyInfo,
    notifyWarning,
    closeNotification
  ]);

  return (
    <NotificationContext.Provider value={value}>
      {children}
      <Snackbar
        open={notification.open}
        autoHideDuration={notification.duration}
        onClose={closeNotification}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert 
          onClose={closeNotification} 
          severity={notification.severity}
          variant="filled"
          sx={{ 
            width: '100%',
            // Use primary theme color for info severity to maintain design consistency
            backgroundColor: notification.severity === 'info' ? theme.palette.primary.main : theme.palette[notification.severity]?.main,
            color: notification.severity === 'info' ? theme.palette.primary.contrastText : (theme.palette[notification.severity]?.contrastText || theme.palette.getContrastText(theme.palette[notification.severity]?.main || theme.palette.common.black)),
            '& .MuiAlert-icon': {
              color: 'inherit'
            },
            '& .MuiAlert-action': {
              color: 'inherit'
            }
          }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </NotificationContext.Provider>
  );
};

NotificationProvider.propTypes = {
  children: PropTypes.node.isRequired
};

export default NotificationContext;
